import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { buildOnlineBankingRows } from './buildOnlineBankAccountForm.helper';
import { OnlineBankAccountHandler, OnlineBankAccountRequestType } from './types';
import { useToken } from '@/providers/Token.provider';
import { useBank } from '@/providers/Bank.provider';
import styles from './OnlineBankAccount.module.scss';
import { usePaylink } from '@/providers/Paylink.provider';

const { Box, Accordion, useForm } = vp;

type OnlineBankAccountFormProps = {
  defaultValues: OnlineBankAccountRequestType;
  handlers: OnlineBankAccountHandler;
  isButtonEnabled: boolean;
  setIsAdditionalFormCompleted: (val: boolean) => void;
};

type FC = FunctionComponent<OnlineBankAccountFormProps>;

const OnlineBankAccountForm: FC = (props: OnlineBankAccountFormProps) => {
  const { defaultValues, handlers, isButtonEnabled, setIsAdditionalFormCompleted } = props;
  const { token } = useToken();
  const { AccountType } = useBank();
  const onlineBankingRows = buildOnlineBankingRows(token?.Country || 'CA');
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const isBankTypeKnown = token?.AccountSelectionMethod !== 'any' && token?.AccountHolderType !== '';
  const { paylink } = usePaylink();
  const [valueSet, setValueSet] = useState<OnlineBankAccountRequestType>({
    IdentificationNumber: '',
    FirstName: '',
    LastName: '',
    CompanyName: '',
    PhoneNumber: paylink.ReceiverPhoneNumber,
  });
  const { t } = useTranslation('translations', { keyPrefix: 'ManualBank' });
  const onSubmit = (values: OnlineBankAccountRequestType) => {
    handlers.saveOnlineBankAccountForm(values);
  };

  useEffect(() => {
    if (isButtonEnabled) {
      onSubmit(valueSet);
    }
  }, [isButtonEnabled]);
  const { renderFormElements, ctx } = useForm<OnlineBankAccountRequestType, OnlineBankAccountRequestType>({
    config: onlineBankingRows,
    initialValues: defaultValues,
    onSubmit: (serializedData) => onSubmit(serializedData),
  });

  useEffect(() => {
    setValueSet(ctx.values);
  }, [ctx.values]);

  useEffect(() => {
    setIsAdditionalFormCompleted(ctx.isValid);
  }, [ctx.isValid]);

  useEffect(() => {
    ctx.setFieldValue('IdentificationNumber', '');
  }, [AccountType]);

  const provideAccountDetail = () => (
    <>
      {!isAccordionOpen &&
      (ctx.values.IdentificationNumber !== '' ||
        ((ctx.values.FirstName !== '' || ctx.values.LastName !== '') && AccountType === 'personal') ||
        (ctx.values.CompanyName !== '' && AccountType === 'business')) ? (
        <Box flx="col" gapSize="4px" height="auto" pt="sm">
          {ctx.values.FirstName && AccountType === 'personal' && (
            <div className={styles.accountDetails}>
              <span>{t('firstName')}:</span>
              <span>{ctx.values.FirstName}</span>
            </div>
          )}
          {ctx.values.LastName && AccountType === 'personal' && (
            <div className={styles.accountDetails}>
              <span>{t('lastName')}:</span>
              <span>{ctx.values.LastName}</span>
            </div>
          )}
          {ctx.values.CompanyName && AccountType === 'business' && (
            <div className={styles.accountDetails}>
              <span>{t('companyName')}:</span>
              <span>{ctx.values.CompanyName}</span>
            </div>
          )}
          {ctx.values.IdentificationNumber && (
            <div className={styles.accountDetails}>
              <span>{AccountType === 'personal' ? `${t('driverLicense')}` : `${t('taxID')}`}:</span>
              <span>{ctx.values.IdentificationNumber}</span>
            </div>
          )}
          {ctx.values.PhoneNumber && (
            <div className={styles.accountDetails}>
              <span>{t('phoneNumber')}:</span>
              <span>{ctx.values.PhoneNumber}</span>
            </div>
          )}
        </Box>
      ) : null}
    </>
  );

  return (
    <form className={styles.form} onSubmit={ctx.handleSubmit}>
      <Accordion
        title={`${isBankTypeKnown ? '2' : '3'}. ${token?.Country === 'US' ? t('provideAccountDetailsTitle') : t('provideAccountDetailsCATitle')}`}
        iconColor={token?.DarkMode ? 'primary-light' : 'primary-dark'}
        iconSize="xsm"
        variant="box"
        titlePadding="0px"
        open={isAccordionOpen}
        onClick={() => {
          setIsAccordionOpen(!isAccordionOpen);
        }}
        subBody={provideAccountDetail()}
        body={
          <div className={styles.formGrid}>
            {renderFormElements({
              config: onlineBankingRows,
            })}
          </div>
        }
      />
    </form>
  );
};

export default OnlineBankAccountForm;
